import { useMemo } from 'react';
import { useGetSheetCategoriesForPeriod } from '../services/sheetCategories.service';
import {
  useGetTransformationsListByPeriod,
  useGetWorkflowExtract
} from '../services/workflow.services';
// @ts-expect-error untyped JS service
import { usePeriodFiles } from '../services/period.service';
import { useGetOutputData } from '../services/outputData.service copy';
// @ts-expect-error untyped JS service
import { useGetDetailedTransformationStatsByPeriodId } from '../services/funds.service';
import { useUserGroupsCurrentUserAdminState } from '../services/user.service';
import { USER_ROLES } from '../consts/userRoles';

/**
 * How many transformation actions a period holds.
 *
 * Two sources, because the actions live in two different places and each kind of period answers from
 * only one of them. A scratch/workbook period keeps them on its sheets — files → sheets →
 * transformations. An automation period's are the set copied from the previous period, which the
 * automation statistics endpoint reports and that one does not.
 *
 * `TransformationV2` already draws exactly this line to render them (`enabled: … && isScratchPeriod`
 * against `enabled: … && !isScratchPeriod`). This asked the workbook endpoint for every period, so an
 * automation period counted zero however many actions it had — and the `transformation` stage gate,
 * which by its own `applies` rule fires ONLY on automation periods, could never open. Report,
 * Approval and Submission sat behind it at a dash, under a tooltip asking for a copy that had
 * already happened.
 *
 * `undefined` while either source is still loading, which the gate reads as "no answer yet" rather
 * than as zero — the gate tests `!== 0`, so a period is never held back on a pending request.
 */
export const useTransformationCount = (
  periodId?: string,
  isAutomationPeriod?: boolean,
  workbookEnabled = true
): number | undefined => {
  const { data: workbook } = (useGetTransformationsListByPeriod as any)({
    periodId,
    queryParams: { enabled: workbookEnabled && !!periodId && !isAutomationPeriod }
  }) as { data?: any[] };
  const { data: automation } = (useGetDetailedTransformationStatsByPeriodId as any)({
    periodId,
    queryParams: { enabled: !!periodId && !!isAutomationPeriod }
  }) as { data?: any[] };

  return useMemo(() => {
    // One row per transformation the automation holds, which is the count itself.
    if (isAutomationPeriod) return Array.isArray(automation) ? automation.length : undefined;
    if (!Array.isArray(workbook)) return undefined;
    let count = 0;
    for (const file of workbook) {
      for (const sheet of file.sheets ?? []) count += (sheet.transformations ?? []).length;
    }
    return count;
  }, [isAutomationPeriod, automation, workbook]);
};

export const useCriticalIssueCount = (periodId?: string, enabled = true): number | undefined => {
  const { data } = (useGetOutputData as any)({
    params: { period_id: periodId },
    queryOptions: { enabled: enabled && !!periodId }
  }) as { data?: any[] };

  return useMemo(() => {
    if (!Array.isArray(data)) return undefined;
    let count = 0;
    for (const category of data) {
      for (const table of category.tables ?? []) count += (table.errors ?? []).length;
    }
    return count;
  }, [data]);
};

export const usePeriodFileCount = (periodId?: string, enabled = true): number | undefined => {
  const { data } = (usePeriodFiles as any)({
    periodId,
    queryOptions: { enabled: enabled && !!periodId }
  }) as { data?: any[] };

  return useMemo(() => {
    if (!Array.isArray(data)) return undefined;
    return data.filter(file => file.period_id === periodId).length;
  }, [data, periodId]);
};

/**
 * How many extractions a period could have: one per sheet mapped to a category.
 *
 * The denominator behind the Extraction page's "0 of 7". No field on the period record answers it —
 * `file_sheet_stats` counts sheets rather than (sheet × category) pairs, and is absent on initial
 * periods entirely.
 *
 * One request per period, cached under `[GET_SHEET_CATEGORIES_FOR_PERIOD, periodId]` — pages already
 * showing the same period (Extraction, Transformation, Mapping) read it from cache.
 */
export const useMappedPairCount = (periodId?: string): number => {
  const { data } = (useGetSheetCategoriesForPeriod as any)({
    periodId,
    queryParams: { enabled: !!periodId }
  }) as { data?: any[] };

  return useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter(
        row => row?.sheet_id && (row?.data_category || row?.custom_category_id)
      ).length,
    [data]
  );
};

/**
 * How many extractions this period actually holds — the numerator of that same "0 of 7", counted off
 * the saved extraction records rather than off `extraction_stats`, which is absent on periods that
 * plainly have extractions.
 *
 * `undefined` until the list has loaded, and the distinction matters: the progress rules refuse to
 * call a period unextracted while this is unknown, so a slow response reads as "no information"
 * rather than "nothing ran".
 */
export const useExtractionRecordCount = (
  workflowId?: string,
  periodId?: string,
  enabled = true
): number | undefined => {
  /* Deliberately no `keepPreviousData`: holding the last period's records across a period switch
     would answer this period's question with another period's count, and the rules would act on it.
     `undefined` while loading is the honest answer, and the one they refuse to act on. */
  const { data: currentUser } = useUserGroupsCurrentUserAdminState() as {
    data?: { role?: string };
  };
  // The extraction list answers approver accounts with a 403, and their stepper shows no extraction.
  const canRead = !!currentUser && currentUser.role !== USER_ROLES.APPROVER;
  const { data } = (useGetWorkflowExtract as any)({
    params: { workflow_id: workflowId, period_id: periodId },
    queryParams: { enabled: enabled && canRead && !!workflowId && !!periodId }
  }) as { data?: any[] };

  return useMemo(() => {
    if (!Array.isArray(data)) return undefined;
    let count = 0;
    for (const file of data) {
      for (const sheet of file?.sheets ?? []) count += sheet?.extractions?.length ?? 0;
    }
    return count;
  }, [data]);
};

/**
 * The period as the progress rules want to read it: its own fields plus the two counts they need.
 *
 * Every surface showing an extraction status wants this, or it will disagree with the ones that have
 * it — the rules fall back to the rolled-up status without it, and that status reports progress on
 * periods holding no extractions and silence on periods holding several.
 */
export const usePeriodWithExtractionCounts = <T>(
  period: T,
  periodId?: string,
  workflowId?: string,
  withOutputIssues = false
): T => {
  const mappedPairCount = useMappedPairCount(periodId);
  const extractionRecordCount = useExtractionRecordCount(workflowId, periodId);
  const periodFileCount = usePeriodFileCount(periodId);
  /* The same test the `transformation` stage gate applies before it fires at all
     (utils/stageGating.ts) — read off the period here so the count comes from the source that kind
     of period actually keeps its actions in. */
  const isAutomationPeriod =
    !!(period as any)?.template_type && (period as any).template_type !== 'sratch';
  const transformationCount = useTransformationCount(periodId, isAutomationPeriod);
  const criticalIssueCount = useCriticalIssueCount(periodId, withOutputIssues);
  return useMemo(
    () =>
      period
        ? ({
            ...period,
            mappedPairCount,
            extractionRecordCount,
            periodFileCount,
            transformationCount,
            criticalIssueCount
          } as T)
        : period,
    [
      period,
      mappedPairCount,
      extractionRecordCount,
      periodFileCount,
      transformationCount,
      criticalIssueCount
    ]
  );
};
